/**
 * webcodecs_decoder.ts
 *
 * Hardware-accelerated H.264 and H.265 decoder using the WebCodecs API.
 * Decodes EncodedVideoChunk → VideoFrame → draws to a shared OffscreenCanvas
 * (or HTMLCanvasElement).
 *
 * Requirements:
 *   - Chrome 94+ / Edge 94+ / Safari 16.4+ / Firefox 130+
 *   - For H.265: hardware decoder must be present (verified by detector.ts)
 *
 * Usage:
 *   const dec = new WebCodecsDecoder(canvas);
 *   dec.configure('h264', 1920, 1080);
 *   dec.decode(naluBytes, isKeyframe, timestampUs);
 *   dec.close();
 */

import { WebCodecsDebugLogger } from './h265_debug';

/**
 * Strip HEVC non-VCL NAL units (VPS/SPS/PPS/SEI/AUD/filler) from an Annex B
 * buffer, returning a new buffer containing only the VCL slice NALs.
 *
 * Motivation: Chrome-Linux's WebCodecs `hev1.*` decoder appears to treat
 * inline VPS+SPS+PPS present in EVERY IDR ACU as a re-configure signal,
 * causing an internal flush. The decoder then rejects the first P-frame
 * following the IDR because its DPB was flushed. Stripping parameter-set NALs
 * from IDR chunks (when the decoder is already configured) prevents this.
 *
 * HEVC non-VCL NUT ranges to strip: 32(VPS), 33(SPS), 34(PPS), 35(APS),
 * 36(EOS), 37(EOB), 38(FD), 39(PREFIX_SEI), 40(SUFFIX_SEI), 41-47(reserved).
 * We keep nut ∈ 0..31 (all slice types including IDR_W_RADL=19, IDR_N_LP=20).
 */
function _stripH265ParamSets(nalu: Uint8Array): Uint8Array {
  const n = nalu.length;
  const out: number[] = [];
  let i = 0;

  while (i < n) {
    // Find start code
    let sc = -1, scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (nalu[j] === 0 && nalu[j + 1] === 0) {
        if (nalu[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && nalu[j + 2] === 0 && nalu[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;

    const nalOff = sc + scLen;
    if (nalOff >= n) break;

    const nut = (nalu[nalOff] >> 1) & 0x3f;

    // Find end of this NAL (start of next start code, or buffer end)
    let nalEnd = n;
    for (let j = nalOff + 1; j < n - 2; j++) {
      if (nalu[j] === 0 && nalu[j + 1] === 0 &&
          (nalu[j + 2] === 1 || (j + 3 < n && nalu[j + 2] === 0 && nalu[j + 3] === 1))) {
        nalEnd = j;
        break;
      }
    }

    // Keep only VCL NALs (nut 0..31 = all slice types)
    if (nut <= 31) {
      // Always write a 4-byte start code for the kept NAL
      out.push(0, 0, 0, 1);
      for (let k = nalOff; k < nalEnd; k++) out.push(nalu[k]);
    }

    i = nalEnd;
  }

  return out.length > 0 ? new Uint8Array(out) : nalu;
}

function _annexBToLengthPrefixed(data: Uint8Array): Uint8Array {
  const units: Uint8Array[] = [];
  const n = data.length;
  let totalSize = 0;
  let i = 0;

  while (i < n) {
    let sc = -1;
    let scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;

    const nalStart = sc + scLen;
    if (nalStart >= n) break;

    let nalEnd = n;
    for (let j = nalStart + 1; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0 &&
          (data[j + 2] === 1 || (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1))) {
        nalEnd = j;
        break;
      }
    }

    const unit = data.subarray(nalStart, nalEnd);
    units.push(unit);
    totalSize += 4 + unit.length;
    i = nalEnd;
  }

  if (units.length === 0) return data;

  const output = new Uint8Array(totalSize);
  const view = new DataView(output.buffer);
  let offset = 0;
  for (const unit of units) {
    view.setUint32(offset, unit.length, false);
    offset += 4;
    output.set(unit, offset);
    offset += unit.length;
  }
  return output;
}

export interface WebCodecsDecoderStats {
  decodedFrames: number;
  droppedFrames: number;
  framesPerSecond?: number;
  frameWidth?: number;
  frameHeight?: number;
}

const CODEC_STRINGS: Record<'h264' | 'h265', string> = {
  h264: 'avc1.42E01E',
  h265: 'hvc1.1.6.L93.B0',
};

export class WebCodecsDecoder {
  private decoder: VideoDecoder | null = null;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null = null;
  private decodedFrames = 0;
  private droppedFrames = 0;
  /** Rolling FPS tracking — 1-second sliding window. */
  private fpsWindowStart = 0;
  private fpsWindowFrames = 0;
  private lastFps = 0;
  /** Last known decoded frame dimensions (auto-updated on resize). */
  private lastFrameWidth = 0;
  private lastFrameHeight = 0;
  /** Require a keyframe before accepting any delta frames after configure()/recovery. */
  private needsKeyframe = true;
  /**
   * True after the first keyframe has been fed to the decoder.
   * Once the decoder has seen the initial IDR (with inline VPS+SPS+PPS so it
   * knows the codec parameters), subsequent IDR ACUs should have their
   * parameter-set NALs stripped to prevent Chrome from internally flushing
   * the decoder and rejecting the following P-frame.
   */
  private _decoderSeenFirstKeyframe = false;
  /** Log frame format/colorspace for first N output frames per decoder instance. */
  private _frameLogCount = 0;
  /** Saved config for transparent recovery after DataError closes the decoder. */
  private lastConfig: VideoDecoderConfig | null = null;
  /** True when H.265 is configured with an HEVCDecoderConfigurationRecord. */
  private usingH265Description = false;
  private debug: WebCodecsDebugLogger;

  /** Listener invoked on every VideoDecoder error event (e.g. DataError on
   *  bad bitstream). HybridPlayerAdapter hooks this to flip its
   *  FrameQualityGate into 'recovering' before a corrupted P-frame can render. */
  private decoderErrorListener: ((err: unknown) => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.debug = new WebCodecsDebugLogger('wc-debug');
    this.debug.setStatsProvider(() => this.getStats());
    this.debug.start();
  }

  onDecoderError(listener: (err: unknown) => void): void {
    this.decoderErrorListener = listener;
  }

  private drawDecodedFrame(frame: VideoFrame): void {
    if (
      frame.displayWidth > 0 &&
      frame.displayHeight > 0 &&
      (this.canvas.width !== frame.displayWidth ||
        this.canvas.height !== frame.displayHeight)
    ) {
      this.canvas.width = frame.displayWidth;
      this.canvas.height = frame.displayHeight;
      this.ctx = this.canvas.getContext('2d');
    }

    const drawDone = (bmp?: ImageBitmap) => {
      if (bmp) {
        this.ctx?.drawImage(bmp, 0, 0, this.canvas.width, this.canvas.height);
        bmp.close();
      } else {
        this.ctx?.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
      }
    };

    if (typeof createImageBitmap !== 'undefined') {
      createImageBitmap(frame as any, { colorSpaceConversion: 'none' } as any)
        .then(drawDone).catch(() => drawDone());
    } else {
      drawDone();
    }

    if (frame.displayWidth > 0) this.lastFrameWidth = frame.displayWidth;
    if (frame.displayHeight > 0) this.lastFrameHeight = frame.displayHeight;
  }

  private createDecoder(): VideoDecoder {
    return new VideoDecoder({
      output: (frame) => {
        this.drawDecodedFrame(frame);
        // Rolling FPS and frame counter — always update, regardless of probe.
        const nowMs = performance.now();
        if (this.fpsWindowStart === 0) this.fpsWindowStart = nowMs;
        this.fpsWindowFrames++;
        const elapsed = nowMs - this.fpsWindowStart;
        if (elapsed >= 1000) {
          this.lastFps = this.fpsWindowFrames / (elapsed / 1000);
          this.fpsWindowStart = nowMs;
          this.fpsWindowFrames = 0;
        }
        this.decodedFrames++;
        // H9 probe: log format/colorspace for first 3 frames.
        if (this._frameLogCount < 3) {
          this._frameLogCount++;
          const cs = (frame as any).colorSpace;
          console.info(
            `[wc-debug] frame#${this._frameLogCount} format=${(frame as any).format ?? 'n/a'}` +
            ` coded=${frame.codedWidth}x${frame.codedHeight}` +
            ` cs.matrix=${cs?.matrix ?? 'n/a'} cs.fullRange=${cs?.fullRange ?? 'n/a'}`,
          );
        }
        // H9 UV probe: on frame#1, defer close() to read chroma planes async.
        if (this._frameLogCount === 1) {
          const w = frame.codedWidth, h = frame.codedHeight;
          const fmts = (['NV12', 'I420', 'RGBA'] as const);
          let pending = fmts.length;
          const done = () => { if (--pending <= 0) frame.close(); };
          for (const fmt of fmts) {
            try {
              const sz = frame.allocationSize({ format: fmt as any });
              const buf = new ArrayBuffer(sz);
              frame.copyTo(buf, { format: fmt as any }).then(() => {
                const arr = new Uint8Array(buf);
                const yLen = w * h, uvPairs = (w >> 1) * (h >> 1);
                const s = Math.min(1000, uvPairs);
                let u = 0, v = 0;
                if (fmt === 'NV12') {
                  for (let k = 0; k < s; k++) { u += arr[yLen+k*2]; v += arr[yLen+k*2+1]; }
                } else if (fmt === 'I420') {
                  for (let k = 0; k < s; k++) { u += arr[yLen+k]; v += arr[yLen+uvPairs+k]; }
                } else {
                  for (let k = 0; k < s; k++) u += arr[k*4+1]; v = u;
                }
                console.info(`[wc-debug] frame#1 ${fmt} U=${(u/s).toFixed(1)} V=${(v/s).toFixed(1)} (neutral≈128)`);
                done();
              }).catch((e: any) => { console.info(`[wc-debug] copyTo(${fmt}) fail: ${e?.message}`); done(); });
            } catch(e: any) {
              console.info(`[wc-debug] alloc(${fmt}) threw: ${e?.message}`);
              done();
            }
          }
          return; // frame.close() handled by probes above
        }
        frame.close(); // must close to release GPU memory
      },
      error: (err) => {
        console.warn('[WebCodecsDecoder] decoder error:', err.message ?? err);
        this.debug.noteError();
        this.droppedFrames++;
        // Decoder goes to 'closed' on DataError — mark needsKeyframe so we
        // transparently re-configure on the next IDR without a hard stop.
        this.needsKeyframe = true;
        try { this.decoderErrorListener?.(err); } catch { /* isolate */ }
      },
    });
  }

  /**
   * Configure the VideoDecoder for the given codec and dimensions.
   * Must be called before the first decode() call.
   * Safe to call again to reconfigure (e.g. resolution change mid-stream).
   *
   * NOTE: width/height may be 0 when the relay sends VIDEO_INIT before it
   * has parsed the SPS (JTT1078 relay sends 0x0 as a placeholder).
   * In that case we omit codedWidth/codedHeight from the config — the
   * WebCodecs VideoDecoder infers dimensions from the SPS in the bitstream.
   * The canvas is auto-resized on the first decoded VideoFrame.
   *
   * @param codecString  Optional explicit codec string derived from the SPS NAL
   *                     (e.g. 'avc1.640028' for High Profile Level 4.0). When
   *                     provided it overrides the conservative default so the
   *                     decoder is configured for the real encoder profile.
   */
  configure(
    codec: 'h264' | 'h265',
    width: number,
    height: number,
    codecString?: string,
    description?: BufferSource,
  ): void {
    this.close();
    this.needsKeyframe = true;

    this.usingH265Description = codec === 'h265' && !!description;
    this.decoder = this.createDecoder();

    // codedWidth/codedHeight are optional — omit when 0 to avoid spec TypeError.
    // WebCodecs infers dimensions from the SPS NAL unit in the bitstream.
    const decoderConfig: VideoDecoderConfig = {
      codec: codecString ?? CODEC_STRINGS[codec],
      optimizeForLatency: true,
    };
    if (width > 0) decoderConfig.codedWidth = width;
    if (height > 0) decoderConfig.codedHeight = height;
    if (description) decoderConfig.description = description;
    this.decoder.configure(decoderConfig);
    this.lastConfig = decoderConfig;
    this.debug.event('configured', { codec: decoderConfig.codec, width, height });
  }

  /**
   * Decode a single NALU.
   *
   * @param nalu         Raw NALU bytes in Annex B format (with start code)
   * @param isKeyframe   true for I-frames (required for first frame and after seek)
   * @param timestampUs  Presentation timestamp in microseconds
   */
  decode(nalu: Uint8Array, isKeyframe: boolean, timestampUs: number): void {
    // If decoder is closed (e.g. after DataError), recover on next keyframe.
    if (this.decoder?.state === 'closed' && isKeyframe && this.lastConfig) {
      console.info('[WebCodecsDecoder] recovering closed decoder on IDR');
      this.debug.noteRecovery();
      // Reset so the recovery IDR is fed WITH inline VPS+SPS+PPS — the freshly
      // created hev1.* decoder needs them to learn codec parameters.
      this._decoderSeenFirstKeyframe = false;
      this._frameLogCount = 0; // re-log format after recovery
      this.decoder = this.createDecoder();
      this.decoder.configure(this.lastConfig);
      this.needsKeyframe = true;
    }

    if (!this.decoder || this.decoder.state === 'closed') return;

    // Drop non-keyframes until the first IDR after configure() or recovery.
    // WebCodecs requires a keyframe immediately after configure().
    if (this.needsKeyframe) {
      if (!isKeyframe) {
        this.droppedFrames++;
        return;
      }
      this.needsKeyframe = false;
    }

    // H6 fix: strip non-VCL parameter-set NALs (VPS/SPS/PPS/SEI) from IDR
    // chunks AFTER the decoder has already seen the first keyframe.
    // Rationale: Chrome-Linux's hev1.* decoder interprets inline VPS+SPS+PPS
    // on every IDR as a re-configure + internal flush. After the flush the
    // decoder rejects the next P-frame with "Decoding error." since its DPB
    // was cleared. On the first IDR we must keep the parameter sets so the
    // decoder learns the codec format; afterwards they are stripped.
    let chunkData: Uint8Array = nalu;
    if (this.usingH265Description) {
      chunkData = _annexBToLengthPrefixed(isKeyframe ? _stripH265ParamSets(nalu) : nalu);
    } else if (isKeyframe) {
      if (this._decoderSeenFirstKeyframe) {
        chunkData = _stripH265ParamSets(nalu);
      } else {
        this._decoderSeenFirstKeyframe = true;
      }
    }

    try {
      // H1 probe: log NAL type for first 8 frames after each recovery so we
      // can detect whether a P-frame ever slips past the needsKeyframe gate.
      // Skip Annex B start code (3 or 4 bytes) to reach the real NAL header.
      const scOff =
        nalu.length >= 4 && nalu[0] === 0 && nalu[1] === 0 && nalu[2] === 0 && nalu[3] === 1
          ? 4
          : nalu.length >= 3 && nalu[0] === 0 && nalu[1] === 0 && nalu[2] === 1
          ? 3
          : 0;
      this.debug.notePostRecoveryNal(isKeyframe, nalu[scOff] ?? 0);
      this.debug.noteDecode(isKeyframe);
      this.decoder.decode(
        new EncodedVideoChunk({
          type: isKeyframe ? 'key' : 'delta',
          timestamp: timestampUs,
          data: chunkData,
        }),
      );
    } catch (err) {
      // Synchronous throws (e.g. InvalidStateError if decoder closed between
      // the state check above and here) — log and reset for next keyframe.
      console.warn('[WebCodecsDecoder] decode() threw:', err);
      this.needsKeyframe = true;
      this.droppedFrames++;
    }
  }

  close(): void {
    if (this.decoder && this.decoder.state !== 'closed') {
      this.decoder.close();
    }
    this.decoder = null;
    this.usingH265Description = false;
    this._decoderSeenFirstKeyframe = false; // next IDR must carry inline params
    this._frameLogCount = 0;
  }

  /**
   * Permanently dispose this decoder (stop the debug logger). Call from the
   * adapter's detach() path. close() alone is not enough because configure()
   * also calls close() to reset the underlying VideoDecoder.
   */
  destroy(): void {
    this.close();
    this.debug.stop();
  }

  getStats(): WebCodecsDecoderStats {
    return {
      decodedFrames: this.decodedFrames,
      droppedFrames: this.droppedFrames,
      framesPerSecond: this.lastFps > 0 ? this.lastFps : undefined,
      frameWidth: this.lastFrameWidth > 0 ? this.lastFrameWidth : undefined,
      frameHeight: this.lastFrameHeight > 0 ? this.lastFrameHeight : undefined,
    };
  }
}
